import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, FileScan, Loader2, AlertTriangle } from "lucide-react";
import {
  Page, Panel, DeleteButton, StatusPill, SearchInput, usePagination, Pagination,
  Modal, ModalActions, Field, SelectField, TextArea, MoneyInput, useSort, SortTh, TotalsBar, HideDailyToggle,
} from "../lib/ui";
import { api, humanDate, money, today, addDays, sumByCur, rowLink } from "../lib/api";
import { StockCombobox, ServiceCombobox, CustomerCombobox, CustomerForm, KDV_OPTIONS, lineTotal, LineTotalInput } from "./Modals";

function OcrDrop({ onParsed }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/purchase-invoices/ocr", fd, { timeout: 180000 });
      const extra = onParsed(data);
      const miss = data.items.filter((x) => !x.match).length;
      setMsg(`${data.items.length} kalem okundu${miss ? ` · ${miss} kalem için kayıtlı kart seçin` : ""}.${extra ? ` ${extra}` : ""} Bilgileri kontrol edin.`);
    } catch (e) { setMsg(e.response?.data?.detail || "Fatura okunamadı"); }
    finally { setBusy(false); }
  };
  return (
    <label className="pi-ocr" data-testid="pi-ocr-drop">
      {busy ? <Loader2 size={20} className="spin" /> : <FileScan size={20} />}
      <strong>{busy ? "Fatura okunuyor…" : "Faturayı yükle (PDF / görsel)"}</strong>
      <small>{msg || "Bilgiler otomatik doldurulur, dosya kaydedilmez"}</small>
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" data-testid="pi-ocr-file" disabled={busy}
             onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
    </label>
  );
}

function OcrHint({ row }) {
  if (!row.ocr_name || row.name) return null;
  return <div className="pi-ocr-hint" data-testid="pi-ocr-hint"><AlertTriangle size={12} /> Faturadaki: <b>{row.ocr_code ? `${row.ocr_code} · ` : ""}{row.ocr_name}</b> — kayıtlı kartı seçin</div>;
}

export default function Purchases({ user, customers = [], accounts = [], stocks = [], services = [] }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("faturali");
  const [month, setMonth] = useState("");
  const [modal, setModal] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/purchase-invoices");
      setRows(data);
    } catch { /* perms */ }
  };
  useEffect(() => { load(); }, []);

  const tabOf = (r) => (r.purchase_type === "Proforma" ? "proforma" : r.purchase_type !== "Faturasiz" && String(r.invoice_no || "").trim() ? "faturali" : "faturasiz");
  const months = useMemo(() => [...new Set(rows.map((r) => String(r.date || "").slice(0, 7)).filter(Boolean))].sort().reverse(), [rows]);
  const monthRows = useMemo(() => (month ? rows.filter((r) => String(r.date || "").startsWith(month)) : rows), [rows, month]);
  const tabCounts = useMemo(() => monthRows.reduce((m, r) => ({ ...m, [tabOf(r)]: (m[tabOf(r)] || 0) + 1 }), {}), [monthRows]);
  const filtered = useMemo(() => monthRows.filter((r) => {
    if (tabOf(r) !== tab) return false;
    if (!q) return true;
    const n = q.toLowerCase();
    return [r.supplier_name, r.invoice_no, r.note]
      .some((v) => (v || "").toLowerCase().includes(n));
  }), [monthRows, q, tab]);
  const srt = useSort(filtered, {
    no: (r) => r.invoice_no || "", supplier: (r) => r.supplier_name || "", date: (r) => r.date, due: (r) => r.due_date || "",
    total: (r) => Number(r.total || 0), status: (r) => r.status,
  });
  const pg = usePagination(srt.sorted);

  const del = async (id) => {
    if (!window.confirm("Alım faturası silinsin mi?")) return;
    await api.delete(`/purchase-invoices/${id}`);
    load();
  };

  const totals = useMemo(() => ({
    total: monthRows.reduce((s, r) => s + (Number(r.total) || 0), 0),
    kdv: monthRows.reduce((s, r) => s + (Number(r.kdv_total) || 0), 0),
    unpaid: monthRows.filter((r) => r.status === "Ödenecek").reduce((s, r) => s + (Number(r.total) || 0), 0),
  }), [monthRows]);
  const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

  return (
    <>
      <Page
        eyebrow="SATIN ALMA"
        title="Alım Faturaları"
        subtitle="Tedarikçi faturalarını kaydedin; alış KDV'si vergi raporuna 'İndirilecek KDV' olarak akar."
        action={<button className="primary-button" data-testid="purchases-new" onClick={() => setModal({ open: true })}><Plus size={15} /> Yeni alım faturası</button>}
      />
      <div className="stat-strip">
        <div className="stat-cell"><span>Toplam alım</span><strong data-testid="pi-total">{money(totals.total)}</strong></div>
        <div className="stat-cell"><span>İndirilecek KDV</span><strong data-testid="pi-kdv">{money(totals.kdv)}</strong></div>
        <div className="stat-cell"><span>Ödenmemiş</span><strong data-testid="pi-unpaid">{money(totals.unpaid)}</strong></div>
      </div>

      <div className="type-toggle install-tabs pi-tabs" data-testid="purchases-tabs">
        {[["faturali", "Fatura no olanlar"], ["proforma", "Proforma alımlar"], ["faturasiz", "Fatura no olmayanlar"]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`purchases-tab-${k}`} className={tab === k ? "active" : ""} onClick={() => setTab(k)}>
            {l} <b>{tabCounts[k] || 0}</b>
          </button>
        ))}
      </div>
      <Panel className="module-panel"
             action={<div className="button-row">
               <select value={month} data-testid="purchases-month-filter" onChange={(e) => setMonth(e.target.value)}
                       style={{ border: "1px solid var(--line)", borderRadius: 10, padding: "8px 10px", fontSize: 13, background: "#fff" }}>
                 <option value="">Tüm aylar</option>
                 {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
               </select>
               <SearchInput value={q} onChange={setQ} testid="purchases-search" placeholder="Tedarikçi, fatura no ara" />
             </div>}>
        <div className="table-wrap">
          <table>
            <thead><tr>
              <SortTh label="Fatura No" k="no" s={srt} testid="pi-sort-no" /><SortTh label="Tedarikçi" k="supplier" s={srt} testid="pi-sort-supplier" />
              <SortTh label="Tarih" k="date" s={srt} testid="pi-sort-date" /><SortTh label="Vade" k="due" s={srt} testid="pi-sort-due" />
              <th className="amount">Ara T.</th><th className="amount">KDV</th>
              <SortTh label="Toplam" k="total" s={srt} align="right" testid="pi-sort-total" /><SortTh label="Durum" k="status" s={srt} testid="pi-sort-status" /><th></th>
            </tr></thead>
            <tbody>
              {pg.slice.length === 0 && <tr><td colSpan="9" className="empty">Alım faturası yok.</td></tr>}
              {pg.slice.map((r) => (
                <tr key={r.id} data-testid={`pi-row-${r.id}`} className="clickable-row"
                    {...rowLink(`/detay/alim/${r.id}`)}>
                  <td><a href={`/detay/alim/${r.id}`} className="row-anchor" onClick={(e) => e.stopPropagation()}><strong>{r.invoice_no || "—"}</strong></a></td>
                  <td>{r.supplier_name}</td>
                  <td>{humanDate(r.date)}</td>
                  <td>{humanDate(r.due_date)}</td>
                  <td className="amount">{money(r.subtotal)}</td>
                  <td className="amount">{money(r.kdv_total)}</td>
                  <td className="amount">{money(r.total)}</td>
                  <td><StatusPill status={r.status} /></td>
                  <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                    {user.role === "admin" && <DeleteButton testid={`pi-delete-${r.id}`} onClick={() => del(r.id)} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TotalsBar testid="purchases-totals" items={[
            { label: `${filtered.length} fatura`, value: "", testid: "purchases-totals-count" },
            { label: "Ara toplam", value: sumByCur(filtered, (r) => r.subtotal), testid: "purchases-totals-sub" },
            { label: "KDV", value: sumByCur(filtered, (r) => r.kdv_total), testid: "purchases-totals-kdv" },
            { label: "Ödenmemiş", value: sumByCur(filtered.filter((r) => r.status === "Ödenecek"), (r) => r.total), tone: "negative", testid: "purchases-totals-unpaid" },
            { label: "Bu sayfa", value: sumByCur(pg.slice, (r) => r.total), testid: "purchases-totals-page" },
            { label: "Genel toplam", value: sumByCur(filtered, (r) => r.total), tone: "grand", testid: "purchases-totals-grand" },
          ]} />
          <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                      onChange={pg.setPage} testid="purchases-pagination" />
        </div>
      </Panel>
      {modal?.open && <PurchaseInvoiceModal customers={customers} accounts={accounts} stocks={stocks} services={services}
                                             onSaved={load} onClose={() => setModal(null)} />}
    </>
  );
}

function LineNote({ row, update, testid, onRemove }) {
  const open = row._showNote ?? !!row.extra_note;
  return (
    <>
      <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
        <button type="button" data-testid={`${testid}-toggle`} className={`line-item-desc-toggle ${open ? "active" : ""}`}
                title="Satıra ek açıklama ekle / kaldır" onClick={() => update({ _showNote: !open })}>
          <Plus size={13} />
        </button>
        <button type="button" className="row-icon tone-danger" onClick={onRemove}><Trash2 size={13} /></button>
      </span>
      {open && (
        <div className="line-item-desc-row">
          <textarea data-testid={testid} placeholder="Bu kalem için ek açıklama"
                    value={row.extra_note || ""} onChange={(e) => update({ extra_note: e.target.value })} />
        </div>
      )}
    </>
  );
}

function PurchaseInvoiceModal({ customers, accounts, stocks, services, onClose, onSaved }) {
  const [form, setForm] = useState({
    supplier_id: "", supplier_name: "",
    invoice_no: "", purchase_type: "Resmi", date: today(), due_date: addDays(today(), 15),
    products: [], services: [],
    status: "Ödenecek", paid_account_id: "", note: "",
  });
  const [error, setError] = useState("");
  const [inlineCustomer, setInlineCustomer] = useState(null);

  const totals = useMemo(() => {
    let sub = 0, kdvT = 0, total = 0;
    for (const row of [...form.products, ...form.services]) {
      const t = lineTotal(row);
      sub += t.sub; kdvT += t.kdvAmount; total += t.total;
    }
    return { subtotal: sub, kdv_total: kdvT, total };
  }, [form.products, form.services]);

  const addProduct = () => setForm({ ...form, products: [...form.products, { stock_id: "", code: "", name: "", description: "", quantity: 1, unit_price: 0, kdv: 20 }] });
  const addService = () => setForm({ ...form, services: [...form.services, { service_id: "", code: "", name: "", description: "", quantity: 1, unit_price: 0, kdv: 20 }] });
  const updateProduct = (i, patch) => { setError(""); setForm({ ...form, products: form.products.map((r, j) => j === i ? { ...r, ...patch } : r) }); };
  const updateService = (i, patch) => { setError(""); setForm({ ...form, services: form.services.map((r, j) => j === i ? { ...r, ...patch } : r) }); };
  const removeProduct = (i) => setForm({ ...form, products: form.products.filter((_, j) => j !== i) });
  const removeService = (i) => setForm({ ...form, services: form.services.filter((_, j) => j !== i) });

  const pickStock = (i, s) => {
    if (!s) return;
    const cur = form.products[i] || {};
    const keep = cur.ocr_name ? { unit_price: cur.unit_price, kdv: cur.kdv } : { unit_price: s.price, kdv: s.kdv ?? 20 };
    updateProduct(i, { stock_id: s.id, code: s.code, name: s.name, ...keep, description: cur.description || s.description || "" });
  };
  const pickService = (i, s) => {
    if (!s) return;
    const cur = form.services[i] || {};
    const keep = cur.ocr_name ? { unit_price: cur.unit_price, kdv: cur.kdv } : { unit_price: s.price, kdv: s.kdv ?? 20 };
    updateService(i, { service_id: s.id, code: s.code, name: s.name, ...keep, description: cur.description || s.description || "" });
  };

  const applyOcr = (d) => {
    const row = (x) => ({
      code: x.match?.code || "", name: x.match?.name || "", description: "", quantity: x.qty || 1,
      unit_price: x.unit_price || 0, kdv: [0, 1, 10, 20].includes(Number(x.kdv)) ? Number(x.kdv) : 20,
      ocr_name: x.ocr_name, ocr_code: x.ocr_code,
      ...(x.is_service ? { service_id: x.match?.id || "" } : { stock_id: x.match?.id || "" }),
    });
    setForm((f) => ({
      ...f,
      supplier_id: d.supplier?.id || f.supplier_id, supplier_name: d.supplier?.name || f.supplier_name,
      invoice_no: f.purchase_type === "Resmi" && d.invoice_no ? d.invoice_no : f.invoice_no,
      date: d.date || f.date, due_date: d.due_date || f.due_date,
      products: [...f.products.filter((r) => r.name), ...d.items.filter((x) => !x.is_service).map(row)],
      services: [...f.services.filter((r) => r.name), ...d.items.filter((x) => x.is_service).map(row)],
    }));
    if (!d.supplier && d.supplier_name) return `Tedarikçi carilerde bulunamadı: ${d.supplier_name}${d.tax_no ? ` (VKN ${d.tax_no})` : ""} — listeden seçin.`;
    return "";
  };

  const submitInlineCustomer = async () => {
    try {
      const { data } = await api.post("/customers", inlineCustomer);
      setForm({ ...form, supplier_id: data.id, supplier_name: data.name });
      setInlineCustomer(null);
    } catch (err) { setError(err.response?.data?.detail || "Cari eklenemedi"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.supplier_name) return setError("Tedarikçi seçin veya yeni cari oluşturun.");
    if (form.purchase_type === "Resmi" && !String(form.invoice_no || "").trim()) return setError("Resmi alım faturası için fatura numarası zorunludur.");
    if (!form.products.length && !form.services.length) return setError("En az bir ürün veya hizmet ekleyin.");
    if (form.status === "Ödendi" && !form.paid_account_id) return setError("Ödeme hesabı seçin.");
    if ([...form.products, ...form.services].some((r) => r.ocr_name && !r.name)) return setError("Faturadan okunan bazı kalemler için kayıtlı stok/hizmet seçilmedi (turuncu uyarılı satırlar). Seçin veya satırı silin.");
    const items = [
      ...form.products.filter((r) => r.name).map((r) => ({
        is_service: false, stock_id: r.stock_id || null, name: r.name, code: r.code,
        description: r.description, extra_note: r.extra_note || "", qty: Number(r.quantity) || 0, unit_price: Number(r.unit_price) || 0, kdv: Number(r.kdv) || 0,
      })),
      ...form.services.filter((r) => r.name).map((r) => ({
        is_service: true, service_id: r.service_id || null, name: r.name, code: r.code,
        description: r.description, extra_note: r.extra_note || "", qty: Number(r.quantity) || 0, unit_price: Number(r.unit_price) || 0, kdv: Number(r.kdv) || 0,
      })),
    ];
    try {
      await api.post("/purchase-invoices", {
        supplier_id: form.supplier_id || null, supplier_name: form.supplier_name,
        invoice_no: form.invoice_no, purchase_type: form.purchase_type, date: form.date, due_date: form.due_date,
        items, subtotal: totals.subtotal, kdv_total: totals.kdv_total, total: totals.total,
        status: form.status, paid_account_id: form.paid_account_id || null, note: form.note, hide_daily: !!form.hide_daily,
      });
      onSaved();
      onClose();
    } catch (err) { setError(err.response?.data?.detail || "Kaydedilemedi"); }
  };

  return (
    <Modal title="Yeni Alım Faturası" size="xl" onClose={onClose} testid="pi-modal">
      <form onSubmit={submit}>
        <div className="pi-top">
        <div className="form-grid three">
          <CustomerCombobox
            customers={customers}
            selectedId={form.supplier_id}
            selectedName={form.supplier_name}
            onSelect={(c) => setForm({ ...form, supplier_id: c?.id || "", supplier_name: c?.name || "" })}
            onCreateNew={() => setInlineCustomer({
              type: "corporate", name: "", tc_no: "", tax_no: "", tax_office: "",
              phone: "", email: "", address: "", city: "", district: "",
            })}
          />
          <SelectField testid="pi-type-select" label="Alım türü" value={form.purchase_type}
                       onChange={async (e) => {
                         const t = e.target.value;
                         let no = t === "Resmi" ? (form.purchase_type === "Resmi" ? form.invoice_no : "") : "";
                         if (t === "Proforma") no = (await api.get(`/purchase-invoices/next-no?date=${form.date}`)).data.invoice_no;
                         setForm({ ...form, purchase_type: t, invoice_no: no });
                       }}>
            <option value="Resmi">Resmi fatura (no zorunlu)</option>
            <option value="Proforma">Proforma alım (otomatik no)</option>
            <option value="Faturasiz">Fatura no olmayan</option>
          </SelectField>
          {form.purchase_type === "Resmi" && (
            <Field testid="pi-invoice-no" label="Fatura no *" value={form.invoice_no}
                   onChange={(e) => setForm({ ...form, invoice_no: e.target.value.toUpperCase() })} />
          )}
          {form.purchase_type === "Proforma" && (
            <Field testid="pi-invoice-no" label="Proforma no (otomatik)" readOnly value={form.invoice_no} hint="A-PRF + yıl ay gün + sıra no" />
          )}
          {form.purchase_type === "Faturasiz" && (
            <div className="field" data-testid="pi-no-invoice-info"><span>Fatura no</span><strong className="muted-cell">Girilmez</strong></div>
          )}
          <SelectField testid="pi-status" label="Durum" value={form.status}
                       onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option>Ödenecek</option>
            <option>Ödendi</option>
            <option>İptal</option>
          </SelectField>
          <Field testid="pi-date" label="Fatura tarihi" type="date" value={form.date}
                 onChange={async (e) => {
                   const date = e.target.value;
                   const no = form.purchase_type === "Proforma" && date
                     ? (await api.get(`/purchase-invoices/next-no?date=${date}`)).data.invoice_no : form.invoice_no;
                   setForm((f) => ({ ...f, date, invoice_no: no }));
                 }} />
          {form.status !== "Ödendi" && (
            <Field testid="pi-due" label="Vade tarihi" type="date" value={form.due_date}
                   onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          )}
          {form.status === "Ödendi" && (
            <SelectField testid="pi-paid-account" label="Ödeme hesabı" value={form.paid_account_id}
                         onChange={(e) => setForm({ ...form, paid_account_id: e.target.value })}>
              <option value="">— Hesap seçin —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name} · {a.type === "cash" ? "Kasa" : a.bank_name || "Banka"}</option>
              ))}
            </SelectField>
          )}
        </div>
        <OcrDrop onParsed={applyOcr} />
        </div>

        {inlineCustomer && (
          <div className="inline-customer">
            <div className="inline-head">
              <strong>Yeni cari</strong>
              <button type="button" className="text-button" onClick={() => setInlineCustomer(null)}>Vazgeç</button>
            </div>
            <CustomerForm form={inlineCustomer} setForm={setInlineCustomer} />
            <div className="inline-actions">
              <button data-testid="pi-inline-customer-save" type="button" className="primary-button" onClick={submitInlineCustomer}>
                <Plus size={15} /> Cariyi kaydet
              </button>
            </div>
          </div>
        )}

        <div className="line-section">
          <div className="line-head">
            <div>
              <strong>Alınan ürünler</strong>
              <p>Stok kartlarından seçim yapın — kaydedilince stoğa girer</p>
            </div>
            <button data-testid="pi-add-product" type="button" className="secondary-button" onClick={addProduct}>
              <Plus size={14} /> Ürün satırı
            </button>
          </div>
          {form.products.length ? (
            <div className="line-table">
              <div className="line-row product pi line-header">
                <span>Stok (kod/ad/açıklama ara)</span><span>Seri No / Açıklama</span><span>Miktar</span><span>Birim ₺</span><span>KDV %</span><span>Toplam</span><span></span>
              </div>
              {form.products.map((row, i) => {
                const t = lineTotal(row);
                const selectedLabel = row.stock_id ? `${row.code || ""} · ${row.name || ""}` : row.name;
                return (
                  <div key={i} className="line-row product pi" data-testid={`pi-product-line-${i}`}>
                    <div>
                      <StockCombobox stocks={stocks} selectedId={row.stock_id} selectedLabel={selectedLabel}
                                     onSelect={(s) => pickStock(i, s)} index={i} />
                      <OcrHint row={row} />
                    </div>
                    <input placeholder="Seri no, açıklama" data-testid={`pi-product-desc-${i}`}
                           value={row.description || ""} onChange={(e) => updateProduct(i, { description: e.target.value })} />
                    <input type="number" step="0.01" value={row.quantity} data-testid={`pi-product-qty-${i}`}
                           onChange={(e) => updateProduct(i, { quantity: e.target.value })} />
                    <MoneyInput decimals={4} value={row.unit_price} data-testid={`pi-product-price-${i}`}
                                onChange={(e) => updateProduct(i, { unit_price: e.target.value })} />
                    <select value={row.kdv} data-testid={`pi-product-kdv-${i}`}
                            onChange={(e) => updateProduct(i, { kdv: e.target.value })}>
                      {KDV_OPTIONS.map((k) => <option key={k} value={k}>%{k}</option>)}
                    </select>
                    <LineTotalInput row={row} testid={`pi-product-total-${i}`} onPatch={(p) => updateProduct(i, p)} />
                    <LineNote testid={`pi-product-note-${i}`} row={row} update={(patch) => updateProduct(i, patch)} onRemove={() => removeProduct(i)} />
                  </div>
                );
              })}
            </div>
          ) : <div className="line-empty">Henüz ürün eklenmedi.</div>}
        </div>

        <div className="line-section">
          <div className="line-head">
            <div>
              <strong>Alınan hizmetler</strong>
              <p>Hizmet kalemleri stoğa yansımaz, sadece gider olarak işlenir</p>
            </div>
            <button data-testid="pi-add-service" type="button" className="secondary-button" onClick={addService}>
              <Plus size={14} /> Hizmet satırı
            </button>
          </div>
          {form.services.length ? (
            <div className="line-table">
              <div className="line-row service line-header">
                <span>Hizmet (kod/ad/açıklama ara)</span><span>Miktar</span><span>Birim ₺</span><span>KDV %</span><span>Toplam</span><span></span>
              </div>
              {form.services.map((row, i) => {
                const t = lineTotal(row);
                const selectedLabel = row.service_id && row.name ? `${row.code || ""} · ${row.name}` : row.name;
                return (
                  <div key={i} className="line-row service" data-testid={`pi-service-line-${i}`}>
                    <div>
                      <ServiceCombobox services={services} selectedLabel={selectedLabel}
                                       onSelect={(s) => pickService(i, s)} index={i} />
                      <OcrHint row={row} />
                    </div>
                    <input type="number" step="0.01" value={row.quantity} data-testid={`pi-service-qty-${i}`}
                           onChange={(e) => updateService(i, { quantity: e.target.value })} />
                    <MoneyInput decimals={4} value={row.unit_price} data-testid={`pi-service-price-${i}`}
                                onChange={(e) => updateService(i, { unit_price: e.target.value })} />
                    <select value={row.kdv} data-testid={`pi-service-kdv-${i}`}
                            onChange={(e) => updateService(i, { kdv: e.target.value })}>
                      {KDV_OPTIONS.map((k) => <option key={k} value={k}>%{k}</option>)}
                    </select>
                    <LineTotalInput row={row} testid={`pi-service-total-${i}`} onPatch={(p) => updateService(i, p)} />
                    <LineNote testid={`pi-service-note-${i}`} row={row} update={(patch) => updateService(i, patch)} onRemove={() => removeService(i)} />
                  </div>
                );
              })}
            </div>
          ) : <div className="line-empty">Henüz hizmet eklenmedi.</div>}
        </div>

        <div className="invoice-totals">
          <div><span>Ara toplam</span><strong>{money(totals.subtotal)}</strong></div>
          <div><span>KDV</span><strong>{money(totals.kdv_total)}</strong></div>
          <div className="grand"><span>Genel toplam</span><strong data-testid="pi-total-amount">{money(totals.total)}</strong></div>
        </div>

        <TextArea testid="pi-note" label="Fatura notu" rows="2" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <HideDailyToggle form={form} setForm={setForm} testid="pi-hide-daily" />

        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel="Faturayı kaydet" submitTestId="pi-submit" />
      </form>
    </Modal>
  );
}
